<template>
    <v-container :fluid="true" class="max-height">
        <v-row>
            <v-col class="px-5" cols="12">
                <h1 class="text-h5 font-weight-bold">Delete Account</h1>
                <p class="text-body-2 text-medium-emphasis mt-1">Close your company account on SimpliGPS</p>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12" md="8">
                <v-alert type="error" variant="tonal" icon="mdi-alert-octagon" class="mb-4">
                    Deleting the account closes it for everyone in your company. Nobody will be able to sign in to
                    the portal again, and the company's data will no longer be available.
                </v-alert>
                <v-btn color="error" prepend-icon="mdi-delete" :loading="loadingCode" @click="openDialog">Delete Account</v-btn>
            </v-col>
        </v-row>

        <v-dialog v-model="dialog" max-width="480" persistent>
            <v-card>
                <v-card-title class="text-h6">Delete {{ companyName }}?</v-card-title>
                <v-card-text>
                    <p class="text-body-2 mb-4">
                        This cannot be undone from the portal. To confirm, type your account number and then
                        DELETE.
                    </p>

                    <v-text-field v-model="accountInput" label="Account number" variant="outlined" density="compact"
                                  autocomplete="off" autofocus
                                  :hint="`Your account number is ${accountCode}. It is also shown on Company Profile.`"
                                  persistent-hint class="mb-4"
                                  :error-messages="accountInput.trim().length >= accountCode.length && !accountMatches ? 'This does not match your account number' : ''"/>

                    <v-text-field v-model="deleteInput" label="Type DELETE" variant="outlined" density="compact"
                                  autocomplete="off" :disabled="!accountMatches"
                                  hint="In capital letters" persistent-hint
                                  :error-messages="deleteInput && !deleteMatches ? 'Type DELETE in capital letters' : ''"/>
                </v-card-text>
                <v-card-actions>
                    <v-spacer/>
                    <v-btn variant="text" :disabled="deleting" @click="dialog = false">Cancel</v-btn>
                    <v-btn color="error" variant="flat" :loading="deleting"
                           :disabled="!accountMatches || !deleteMatches || deleting" @click="deleteAccount">
                        Delete account
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script setup>
import {computed, onMounted, ref} from "vue";
import {send} from "@/composables/apiClient";
import {useAuthStore} from "@/stores/authStore";
import {useNavigation} from '@/composables/globals';
import {useSnackbarStore} from "@/stores/snackBarStore";

const {showSnackbar} = useSnackbarStore();
const {navigateTo} = useNavigation();
const authStore = useAuthStore();
const {user} = authStore;

const dialog = ref(false);
const deleting = ref(false);
const accountInput = ref('');
const deleteInput = ref('');

// The login payload has no account number, so it is loaded from the company when the dialog opens
const accountCode = ref('');
const loadingCode = ref(false);
const companyName = computed(() => user?.company?.company_name || 'your company');
const accountMatches = computed(() => !!accountCode.value && accountInput.value.trim() === accountCode.value);
const deleteMatches = computed(() => deleteInput.value === 'DELETE');

onMounted(() => {
    if (user.id !== user.company.owner_id) {
        navigateTo('dashboard', {})
    }
})

const openDialog = async () => {
    accountInput.value = '';
    deleteInput.value = '';
    if (!accountCode.value) {
        loadingCode.value = true;
        try {
            const res = await send('get', 'companies/user-company');
            accountCode.value = String(res?.data?.data?.company?.account_code ?? '');
        } catch {
            // handled below
        } finally {
            loadingCode.value = false;
        }
    }
    if (!accountCode.value) {
        showSnackbar('Your account number could not be loaded. Please try again.', 'error');
        return;
    }
    dialog.value = true;
}

const deleteAccount = () => {
    if (!accountMatches.value || !deleteMatches.value) return;
    deleting.value = true;
    send('delete', `owner/companies/disable`, {data: {account_code: accountInput.value.trim(), confirm: deleteInput.value}})
        .then(() => {
            authStore.logout();
        })
        .catch((error) => {
            showSnackbar(error?.response?.data?.message ?? 'The account could not be deleted', 'error')
        })
        .finally(() => {
            deleting.value = false;
        })
}
</script>
